import { useState } from "react";
import { UserPlus, PenLine, Send, Check, X, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PARTY_ROLES } from "@/data/exchange";

const ROLE_LABEL = Object.fromEntries(PARTY_ROLES.map((r) => [r.role, r.label]));
const DOC_LABEL = { exchange_agreement: "Exchange Agreement", assignment: "Assignment of Contract", designation_notice: "Designation (45-day ID) Notice", escrow_addendum: "Escrow Instructions Addendum", cert_citizenship: "Certificate of Citizenship", exchange_summary: "Exchange Summary" };

const ACTION_RE = /```ally-action\s*([\s\S]*?)```/g;

// Split an assistant reply into visible text and proposed actions; hides a half-streamed action block.
export function parseAllyReply(text) {
  const actions = [];
  let visible = text.replace(ACTION_RE, (_, body) => {
    try { actions.push(JSON.parse(body.trim())); } catch { /* ignore malformed */ }
    return "";
  });
  const open = visible.indexOf("```ally-action");
  if (open !== -1) visible = visible.slice(0, open);
  return { visible: visible.replace(/\n{3,}/g, "\n\n").trim(), actions };
}

const META = {
  add_party: { icon: UserPlus, label: "Add party", verb: "Added" },
  request_signature: { icon: PenLine, label: "Request signature", verb: "Signature request sent" },
  send_update: { icon: Send, label: "Send update", verb: "Sent" },
};

async function run(a) {
  const base = `/enterprise/exchanges/${a.exchange_id}`;
  if (a.type === "add_party") {
    const { data: ex } = await api.get(base);
    await api.put(base, { parties: [...(ex.parties || []).filter((p) => p.role !== a.party.role), a.party] });
  } else if (a.type === "request_signature") {
    await api.post(`${base}/signatures`, { doc_type: a.doc_type, signer_name: a.signer_name, signer_email: a.signer_email });
  } else if (a.type === "send_update") {
    await api.post(`${base}/send-update`, { channel: a.channel || "email", subject: a.subject || "", message: a.message, to: a.to || null });
  } else throw new Error("Unknown action");
  window.dispatchEvent(new CustomEvent("ally:exchange-updated", { detail: { exchangeId: a.exchange_id } }));
}

const Details = ({ a }) => {
  const rows = a.type === "add_party"
    ? [["Role", ROLE_LABEL[a.party?.role] || a.party?.role], ["Name", a.party?.name], ["Company", a.party?.company], ["Email", a.party?.email], ["Phone", a.party?.phone]]
    : a.type === "request_signature"
      ? [["Document", DOC_LABEL[a.doc_type] || a.doc_type], ["Signer", a.signer_name], ["Email", a.signer_email]]
      : [["Channel", a.channel], ["To", a.to || "Client on file"], ["Subject", a.subject]];
  return (
    <div className="mt-2 space-y-0.5 text-xs">
      {rows.filter(([, v]) => v).map(([k, v]) => <div key={k}><span className="text-ally-slate">{k}:</span> <span className="font-semibold">{v}</span></div>)}
      {a.type === "send_update" && a.message && <div className="mt-2 p-2 rounded-lg bg-[#FAFAFA] border border-black/5 whitespace-pre-wrap max-h-40 overflow-y-auto">{a.message}</div>}
    </div>
  );
};

// status persists in the parent message so closing the panel can't re-arm a finished card
export function AllyActionCard({ action, testid, status = "pending", onStatus, onOutcome }) {
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState("");
  const m = META[action.type];
  if (!m) return null;
  const state = running ? "running" : status;
  const setState = (s) => onStatus?.(s);
  const confirm = async () => {
    setRunning(true); setErr("");
    try { await run(action); setState("done"); onOutcome?.(`Confirmed and completed: ${action.summary}`); }
    catch (e) { setErr(apiError(e) || e.message); onOutcome?.(`Tried but FAILED: ${action.summary} (${apiError(e) || e.message})`); }
    finally { setRunning(false); }
  };
  const I = m.icon;
  return (
    <div data-testid={testid} className="mt-2 bg-white border-2 border-ally-pink/30 rounded-2xl p-3 text-ally-ink max-w-[85%]">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-ally-pink"><I size={14} /> {m.label}</div>
      <div className="text-sm font-semibold mt-1">{action.summary}</div>
      <Details a={action} />
      {err && <div data-testid={`${testid}-error`} className="text-xs text-red-600 mt-2">{err}</div>}
      {state === "done" ? (
        <div data-testid={`${testid}-done`} className="mt-3 text-xs font-bold text-green-700 flex items-center gap-1"><Check size={14} /> {m.verb}</div>
      ) : state === "cancelled" ? (
        <div data-testid={`${testid}-cancelled`} className="mt-3 text-xs text-ally-slate">Cancelled. Nothing was changed.</div>
      ) : (
        <div className="mt-3 flex gap-2">
          <button data-testid={`${testid}-confirm`} onClick={confirm} disabled={state === "running"}
            className="inline-flex items-center gap-1 bg-ally-pink text-white text-xs font-bold rounded-full px-4 py-1.5 hover:bg-ally-ink transition-colors disabled:opacity-60">
            {state === "running" ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Confirm
          </button>
          <button data-testid={`${testid}-cancel`} onClick={() => { setState("cancelled"); onOutcome?.(`Cancelled by user, nothing changed: ${action.summary}`); }} disabled={state === "running"}
            className="inline-flex items-center gap-1 border border-black/10 text-xs font-bold rounded-full px-4 py-1.5 hover:border-ally-ink transition-colors"><X size={12} /> Cancel</button>
        </div>
      )}
    </div>
  );
}
